import type { PostHog, Properties } from 'posthog-js'

/**
 * Guarded access to PostHog for app code (composables, stores), the same shape
 * as `utils/faro`: type-only imports, so nothing here drags `posthog-js` into
 * the entry chunk or the server bundle. `posthog.client.ts` hands the instance
 * over once its dynamic import resolves.
 *
 * Unlike Faro, calls made while the SDK is still loading are queued rather than
 * dropped: a product page fires `view_item` on mount, usually before the chunk
 * has arrived, and that first view is the top of every funnel. The queue only
 * exists once the plugin has decided PostHog is on, so during SSR, outside
 * production and without a token every call here stays a no-op.
 */
type PostHogCall = (posthog: PostHog) => void

let instance: PostHog | undefined
let pending: PostHogCall[] | undefined

/** Bounded, so an SDK chunk that never arrives (blocked, offline) cannot grow it forever. */
const MAX_PENDING_CALLS = 100

/** Start holding calls until `setPostHogInstance`. Called by the plugin once it knows PostHog is on. */
export function queuePostHogCalls() {
  pending ??= []
}

export function setPostHogInstance(posthog: PostHog) {
  instance = posthog
  const calls = pending ?? []
  pending = undefined
  calls.forEach(runPostHogCall)
}

/** The SDK failed to load: stop holding calls, nothing will ever replay them. */
export function dropPostHogCalls() {
  pending = undefined
}

function runPostHogCall(call: PostHogCall) {
  if (!instance) {
    if (pending && pending.length < MAX_PENDING_CALLS)
      pending.push(call)
    return
  }

  try {
    call(instance)
  }
  catch {
    // Analytics never throws into business logic.
  }
}

export function capturePostHogEvent(name: string, properties?: Properties) {
  // Stamped now, not when a queued call replays, so an event fired before the
  // SDK arrived keeps its real place in the session.
  const timestamp = new Date()
  runPostHogCall(posthog => posthog.capture(name, properties, { timestamp }))
}

/**
 * Ties this browser's history to a Vendure customer, by id only: the same id
 * GA4 gets as `user_id`, it survives an e-mail change, and no e-mail or name is
 * sent because there is no consent-management platform in front of any of this
 * (see `lead` in useAnalytics). Repeating the current id is a no-op in
 * posthog-js; a different id switches person without merging the two.
 */
export function identifyPostHogCustomer(customerId: string | number | null | undefined) {
  if (customerId == null || customerId === '')
    return
  const distinctId = String(customerId)
  runPostHogCall(posthog => posthog.identify(distinctId))
}

/**
 * Forget the identified customer and start a fresh anonymous visitor. Only an
 * explicit logout may call this: resetting on anything that merely looks logged
 * out (an expired session on page load) splits one returning shopper into two
 * people and cuts their session in half.
 */
export function resetPostHog() {
  runPostHogCall(posthog => posthog.reset())
}
